/**
 * API модуля security: 2FA, PIN, feature flags.
 */

import { get, post, patch } from "@/lib/api-client-v2";

export type SecurityStatus = {
  two_factor_module_enabled: boolean;
  screen_lock_module_enabled: boolean;
  require_2fa_for_all: boolean;
  pin_min_length: number;
  pin_max_length: number;
  idle_timeout_min_seconds: number;
  max_pin_attempts: number;
  lockout_duration_seconds: number;
};

export type UserSecurityMe = {
  two_factor_enabled: boolean;
  screen_lock_enabled: boolean;
  idle_timeout_seconds: number | null;
  effective_idle_timeout_seconds: number;
  pin_configured: boolean;
  must_enroll_2fa: boolean;
};

export type SecurityFeatureFlags = {
  two_factor_enabled: boolean;
  screen_lock_enabled: boolean;
  require_2fa_for_all: boolean;
  pin_min_length: number;
  pin_max_length: number;
  max_pin_attempts: number;
  lockout_duration_seconds: number;
  idle_timeout_min_seconds: number;
  mfa_challenge_ttl_seconds: number;
  screen_unlock_ttl_seconds: number;
  updated_at?: string | null;
};

export type LoginResponse = {
  access_token?: string | null;
  token_type?: string;
  expires_in?: number | null;
  mfa_required?: boolean;
  mfa_token?: string | null;
  must_enroll_2fa?: boolean;
  scope?: string | null;
};

export const securityApi = {
  status: () => get<SecurityStatus>("/security/status"),
  me: () => get<UserSecurityMe>("/security/me"),

  setup2fa: () =>
    post<{ otpauth_uri: string; qr_code_data_url: string; expires_in: number }>(
      "/security/2fa/setup",
      {}
    ),
  verify2fa: (code: string) =>
    post<{ recovery_codes: string[]; access_token?: string; expires_in?: number }>(
      "/security/2fa/verify",
      { code }
    ),
  disable2fa: (body: {
    password?: string;
    totp_code?: string;
    recovery_code?: string;
  }) => post<void>("/security/2fa/disable", body),

  setupPin: (pin: string, idle_timeout_seconds?: number) =>
    post<UserSecurityMe>("/security/pin/setup", { pin, idle_timeout_seconds }),
  verifyPin: (pin: string) =>
    post<{ unlock_token: string; expires_in: number; scope: string }>(
      "/security/pin/verify",
      { pin }
    ),
  changePin: (body: { old_pin?: string; new_pin: string; totp_code?: string }) =>
    post<UserSecurityMe>("/security/pin/change", body),
  disablePin: (body: { password?: string; totp_code?: string }) =>
    post<UserSecurityMe>("/security/pin/disable", body),
  forgotPin: () =>
    post<{ message: string; hard_sign_out: boolean }>("/security/pin/forgot", {}),

  getFeatureFlags: () => get<SecurityFeatureFlags>("/admin/security/feature-flags"),
  patchFeatureFlags: (body: Partial<SecurityFeatureFlags>) =>
    patch<SecurityFeatureFlags>("/admin/security/feature-flags", body),

  mfaVerify: (mfa_token: string, code: string) =>
    post<{ access_token: string; token_type: string; expires_in: number }>(
      "/auth/mfa/verify",
      { mfa_token, code }
    ),
};
